import { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Alert, Box, Button, Checkbox, CircularProgress, FormControlLabel, Link, TextField, Typography } from '@mui/material';
import { collection, getDocs, limit, query, where } from 'firebase/firestore';
import { db } from '../../firebaseConfig';
import logoSanLorenzo from '../../assets/logo-san-lorenzo.png';

const ENDPOINT = 'https://europe-west1-cassesanlorenzo.cloudfunctions.net/iscrizionePromoLanding';

const fieldSx = {
  '& .MuiInputBase-input': { fontSize: 16 }
};

const vuoto = {
  nome: '',
  cognome: '',
  email: '',
  telefono: '',
  privacy: true,
  marketing: true
};

const origineDallaUrl = () => {
  const params = new URLSearchParams(window.location.search);
  return {
    utm_source: params.get('utm_source') || '',
    utm_medium: params.get('utm_medium') || '',
    utm_campaign: params.get('utm_campaign') || '',
    utm_content: params.get('utm_content') || '',
    utm_term: params.get('utm_term') || '',
    fbclid: params.get('fbclid') || '',
    negozio_id: params.get('negozio') || params.get('utm_content') || ''
  };
};

function BadgeOmaggio({ promo }) {
  const etichetta = String(promo?.badge_etichetta || '').trim();
  const importo = String(promo?.badge_importo || '').trim();
  const frase = String(promo?.badge_frase || '').trim();
  const nota = String(promo?.badge_nota || '').trim();
  if (!etichetta && !importo && !frase && !nota) return null;
  const pill = etichetta.includes('🎁') ? etichetta : `🎁 ${etichetta}`;
  return (
    <Box sx={{ mt: 2 }}>
      {etichetta && (
        <Box
          component="span"
          sx={{
            display: 'inline-block',
            bgcolor: '#e4b84a',
            color: '#2b2118',
            fontWeight: 700,
            borderRadius: 999,
            px: 2.2,
            py: 0.7,
            fontSize: 14,
            letterSpacing: 0.3
          }}
        >
          {pill}
        </Box>
      )}
      {importo && <Typography sx={{ mt: 1.2, fontSize: 32, lineHeight: 1.1, color: '#2b2118' }}>{importo}</Typography>}
      {frase && (
        <Typography sx={{ mt: 0.4, fontWeight: 800, color: '#c0392b', fontSize: 18, letterSpacing: 0.2 }}>{frase}</Typography>
      )}
      {nota && <Typography sx={{ mt: 0.7, fontSize: 16 }}>{nota}</Typography>}
    </Box>
  );
}

export default function LandingPromo() {
  const { slug } = useParams();
  const [promo, setPromo] = useState(null);
  const [statoPromo, setStatoPromo] = useState('carico');
  const [form, setForm] = useState(vuoto);
  const [invio, setInvio] = useState(false);
  const [errore, setErrore] = useState('');
  const [esito, setEsito] = useState(null);
  const [privacyAperta, setPrivacyAperta] = useState(false);

  useEffect(() => {
    let cancel = false;
    const carica = async () => {
      try {
        const snap = await getDocs(query(collection(db, 'promozioni'), where('slug', '==', slug), limit(5)));
        const trovata = snap.docs
          .map((item) => ({ id: item.id, ...item.data() }))
          .find((item) => item.tipo === 'soglia_omaggio' && item.attiva !== false);
        if (cancel) return;
        if (!trovata) {
          setStatoPromo('assente');
          return;
        }
        setPromo(trovata);
        setStatoPromo('pronta');
      } catch (err) {
        console.error(err);
        if (!cancel) setStatoPromo('errore');
      }
    };
    carica();
    return () => {
      cancel = true;
    };
  }, [slug]);

  useEffect(() => {
    if (statoPromo !== 'pronta' || !slug) return undefined;
    const chiave = `promo-visita-${slug}`;
    if (window.sessionStorage.getItem(chiave)) return undefined;
    window.sessionStorage.setItem(chiave, '1');
    fetch(ENDPOINT, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ azione: 'visita', slug })
    }).catch(() => {});
    return undefined;
  }, [slug, statoPromo]);

  const soglia = useMemo(() => Number(promo?.spesa_minima_euro) || 39, [promo]);
  const giorni = promo?.durata_giorni || 10;
  const bottiglia = promo?.prodotto_omaggio_nome || 'una bottiglia';

  const cambia = (campo) => (event) => {
    const value = event.target.type === 'checkbox' ? event.target.checked : event.target.value;
    setForm((prev) => ({ ...prev, [campo]: value }));
  };

  const invia = async (event) => {
    event.preventDefault();
    setErrore('');
    setInvio(true);
    try {
      const response = await fetch(ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          slug,
          nome: form.nome,
          cognome: form.cognome,
          email: form.email,
          telefono: form.telefono,
          accetta_condizioni: true,
          accetta_privacy: form.privacy,
          consenso_marketing: form.marketing,
          origine: origineDallaUrl()
        })
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok && !data.esito) {
        setErrore("Non siamo riusciti a registrare l'iscrizione. Riprova tra poco.");
        return;
      }
      if (data.esito === 'non_valida' || data.esito === 'errore' || data.esito === 'non_disponibile') {
        setErrore(data.messaggio || 'Controlla i dati e riprova.');
        return;
      }
      setEsito(data);
    } catch (err) {
      console.error(err);
      setErrore('Connessione assente. Riprova tra poco.');
    } finally {
      setInvio(false);
    }
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f4efe8', color: '#2b2118', px: 2, py: 3 }}>
      <Box sx={{ maxWidth: 460, mx: 'auto' }}>
        <Box
          component="img"
          src={logoSanLorenzo}
          alt=""
          sx={{ width: '100%', maxWidth: 220, display: 'block', mx: 'auto', mixBlendMode: 'multiply' }}
        />
        <Typography
          component="p"
          sx={{ m: 0, textAlign: 'center', color: '#1e2a78', fontWeight: 700, letterSpacing: 0.6, fontSize: 22 }}
        >
          San Lorenzo Vini
        </Typography>
        {statoPromo === 'carico' && (
          <Box sx={{ py: 8, textAlign: 'center' }}>
            <CircularProgress sx={{ color: '#7a1f2b' }} />
          </Box>
        )}
        {statoPromo === 'assente' && (
          <Alert severity="info" sx={{ mt: 3 }}>
            Questa offerta non è attiva.
          </Alert>
        )}
        {statoPromo === 'errore' && (
          <Alert severity="error" sx={{ mt: 3 }}>
            Non riusciamo a caricare la pagina. Riprova tra poco.
          </Alert>
        )}
        {statoPromo === 'pronta' && !esito && (
          <Box component="form" onSubmit={invia} sx={{ textAlign: 'center' }}>
            <Typography variant="h4" sx={{ fontWeight: 700, mt: 1, lineHeight: 1.15, textAlign: 'center', whiteSpace: 'pre-wrap' }}>
              {promo.titolo || 'Una bottiglia in regalo!'}
            </Typography>
            <BadgeOmaggio promo={promo} />
            <Typography sx={{ mt: 1.5, fontSize: 17, lineHeight: 1.45, textAlign: 'center', whiteSpace: 'pre-wrap' }}>
              {promo.descrizione || `Con una spesa minima di ${soglia} euro ricevi ${bottiglia} in omaggio nei nostri punti vendita.`}
            </Typography>
            <Typography variant="body2" sx={{ mt: 1, color: '#5c5148', textAlign: 'center' }}>
              Vale {giorni} giorni dall&apos;iscrizione, una sola volta, in qualsiasi negozio. L&apos;offerta è per chi non ha ancora
              acquistato da noi.
            </Typography>
            <Box sx={{ display: 'grid', gap: 1.5, mt: 3 }}>
              <TextField label="Nome" value={form.nome} onChange={cambia('nome')} required fullWidth sx={fieldSx} />
              <TextField label="Cognome" value={form.cognome} onChange={cambia('cognome')} required fullWidth sx={fieldSx} />
              <TextField label="Email" type="email" value={form.email} onChange={cambia('email')} required fullWidth sx={fieldSx} />
              <TextField label="Telefono (facoltativo)" value={form.telefono} onChange={cambia('telefono')} fullWidth sx={fieldSx} />
              <Typography variant="caption" sx={{ color: '#5c5148', lineHeight: 1.4, textAlign: 'center' }}>
                Spesa minima per ricevere {bottiglia} in omaggio: {soglia} euro, bottiglia esclusa. Un solo ritiro entro {giorni} giorni.
              </Typography>
              <FormControlLabel
                sx={{
                  m: 0,
                  width: '100%',
                  alignItems: 'flex-start',
                  justifyContent: 'center',
                  '& .MuiFormControlLabel-label': { textAlign: 'center' }
                }}
                control={<Checkbox checked={form.privacy} onChange={cambia('privacy')} required />}
                label={
                  <span>
                    Ho letto l&apos;informativa privacy.{' '}
                    <Link
                      component="button"
                      type="button"
                      onClick={(event) => {
                        event.preventDefault();
                        event.stopPropagation();
                        setPrivacyAperta((open) => !open);
                      }}
                    >
                      {privacyAperta ? 'Nascondi' : 'Leggi'}
                    </Link>
                  </span>
                }
              />
              {privacyAperta && (
                <Typography variant="body2" sx={{ color: '#5c5148', textAlign: 'center' }}>
                  Usiamo nome, cognome, email e, se lo indichi, il telefono per riconoscere la promozione in cassa e inviarti il riepilogo
                  dell&apos;offerta. I dati restano nell&apos;anagrafica clienti dei punti vendita San Lorenzo Vini. L&apos;iscrizione alla
                  newsletter è separata e facoltativa. Puoi chiedere la cancellazione scrivendo a sanlorenzovini@gmail.com.
                </Typography>
              )}
              <FormControlLabel
                sx={{
                  m: 0,
                  width: '100%',
                  alignItems: 'flex-start',
                  justifyContent: 'center',
                  '& .MuiFormControlLabel-label': { textAlign: 'center' }
                }}
                control={<Checkbox checked={form.marketing} onChange={cambia('marketing')} />}
                label="Voglio ricevere novità e offerte. Posso farne a meno e la bottiglia, se mi spetta, resta valida."
              />
              {errore && <Alert severity="warning">{errore}</Alert>}
              <Button
                type="submit"
                variant="contained"
                disabled={invio}
                sx={{ bgcolor: '#7a1f2b', py: 1.4, fontSize: 16, '&:hover': { bgcolor: '#641824' } }}
              >
                {invio ? <CircularProgress size={22} sx={{ color: '#fff' }} /> : 'Ricevi la bottiglia'}
              </Button>
            </Box>
          </Box>
        )}
        {esito && (
          <Box sx={{ mt: 2, textAlign: 'center' }}>
            <Typography variant="h4" sx={{ fontWeight: 700, lineHeight: 1.15, textAlign: 'center' }}>
              {esito.esito === 'gia_cliente' ? 'Ci conosciamo già' : 'Bottiglia riservata'}
            </Typography>
            <Typography sx={{ mt: 2, fontSize: 17, lineHeight: 1.5, textAlign: 'center' }}>{esito.messaggio}</Typography>
            {esito.esito !== 'gia_cliente' && (
              <Box sx={{ mt: 2 }}>
                <Typography>
                  Omaggio: <strong>{esito.prodotto || bottiglia}</strong>
                </Typography>
                <Typography sx={{ mt: 0.5 }}>
                  Spesa minima: <strong>{esito.spesa_minima_euro || soglia} euro</strong>, bottiglia esclusa.
                </Typography>
                {Array.isArray(esito.negozi) && esito.negozi.length > 0 && (
                  <Typography sx={{ mt: 1.5 }}>Punti vendita: {esito.negozi.join(', ')}.</Typography>
                )}
              </Box>
            )}
            <Typography variant="body2" sx={{ mt: 2, color: '#5c5148', textAlign: 'center' }}>
              {esito.email_inviata
                ? 'Ti abbiamo scritto anche via email. Se non la vedi, controlla la posta indesiderata.'
                : esito.email_gia_inviata
                  ? 'Ti abbiamo già scritto nelle ultime 24 ore. Controlla anche la posta indesiderata.'
                  : esito.esito === 'gia_cliente'
                    ? "L'omaggio non è stato emesso. Vale quello che leggi qui, anche se la mail non arriva."
                    : 'Se non vedi la mail entro qualche minuto, tieni questa schermata: la promozione è comunque registrata.'}
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
}
